import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Cat, ArrowLeft, Trophy, Star, Volume2,
  RotateCcw, Sparkles, Heart
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const LetterSoundsSafariGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [currentLetter, setCurrentLetter] = useState(null);
  const [options, setOptions] = useState([]);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [timeLeft, setTimeLeft] = useState(60); // 1 minute timer

  // Letter sounds with phonetic representations and images
  const letterData = {
    'A': { sound: 'ah', word: 'ant', animal: '🐜', phonetic: '/æ/' },
    'B': { sound: 'buh', word: 'bear', animal: '🐻', phonetic: '/b/' },
    'C': { sound: 'kuh', word: 'cat', animal: '🐱', phonetic: '/k/' },
    'D': { sound: 'duh', word: 'dog', animal: '🐕', phonetic: '/d/' },
    'E': { sound: 'eh', word: 'elephant', animal: '🐘', phonetic: '/ɛ/' },
    'F': { sound: 'fff', word: 'fish', animal: '🐟', phonetic: '/f/' },
    'G': { sound: 'guh', word: 'goat', animal: '🐐', phonetic: '/g/' },
    'H': { sound: 'huh', word: 'horse', animal: '🐴', phonetic: '/h/' },
    'I': { sound: 'ih', word: 'iguana', animal: '🦎', phonetic: '/ɪ/' },
    'J': { sound: 'juh', word: 'jellyfish', animal: '🪼', phonetic: '/dʒ/' },
    'K': { sound: 'kuh', word: 'koala', animal: '🐨', phonetic: '/k/' },
    'L': { sound: 'lll', word: 'lion', animal: '🦁', phonetic: '/l/' },
    'M': { sound: 'mmm', word: 'monkey', animal: '🐵', phonetic: '/m/' },
    'N': { sound: 'nnn', word: 'newt', animal: '🦎', phonetic: '/n/' },
    'O': { sound: 'oh', word: 'octopus', animal: '🐙', phonetic: '/ɒ/' },
    'P': { sound: 'puh', word: 'pig', animal: '🐷', phonetic: '/p/' },
    'Q': { sound: 'kwuh', word: 'quail', animal: '🐦', phonetic: '/kw/' },
    'R': { sound: 'rrr', word: 'rabbit', animal: '🐰', phonetic: '/r/' },
    'S': { sound: 'sss', word: 'snake', animal: '🐍', phonetic: '/s/' },
    'T': { sound: 'tuh', word: 'tiger', animal: '🐯', phonetic: '/t/' },
    'U': { sound: 'uh', word: 'unicorn', animal: '🦄', phonetic: '/ʌ/' },
    'V': { sound: 'vvv', word: 'vulture', animal: '🦅', phonetic: '/v/' },
    'W': { sound: 'wuh', word: 'whale', animal: '🐋', phonetic: '/w/' },
    'X': { sound: 'ks', word: 'fox', animal: '🦊', phonetic: '/ks/' },
    'Y': { sound: 'yuh', word: 'yak', animal: '🦬', phonetic: '/j/' },
    'Z': { sound: 'zzz', word: 'zebra', animal: '🦓', phonetic: '/z/' },
  };

  // Timer effect
  useEffect(() => {
    let timer;
    if (gameState === 'playing' && timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            setGameState('finished');
            return 0;
          }
          return t - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameState, timeLeft]);

  const generateQuestion = useCallback(() => {
    const letters = Object.keys(letterData);
    const correctLetter = letters[Math.floor(Math.random() * letters.length)];
    
    // Get wrong options
    const wrongLetters = letters
      .filter(l => l !== correctLetter)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
    
    // Shuffle all options
    const allOptions = [correctLetter, ...wrongLetters].sort(() => Math.random() - 0.5);
    
    setCurrentLetter(correctLetter);
    setOptions(allOptions);
  }, []);

  const playSound = async (letter) => {
    if (isPlaying) return;
    setIsPlaying(true);
    
    try {
      const data = letterData[letter];
      const text = `${data.sound}. ${letter} is for ${data.word}.`;
      
      const response = await api.post('/tts/generate', {
        text: text,
        voice: 'nova'
      });
      
      if (response.data?.audio) {
        const audio = new Audio(`data:audio/mp3;base64,${response.data.audio}`);
        audio.onended = () => setIsPlaying(false);
        audio.play();
      } else {
        setIsPlaying(false);
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsPlaying(false);
      // Fallback: use browser speech synthesis
      const utterance = new SpeechSynthesisUtterance(letterData[letter].sound);
      utterance.rate = 0.8;
      speechSynthesis.speak(utterance);
    }
  };

  const startGame = useCallback(() => {
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setCorrectAnswers(0);
    setTimeLeft(60); // Reset timer to 1 minute
    generateQuestion();
  }, [generateQuestion]);

  const handleAnswer = (selectedLetter) => {
    if (selectedLetter === currentLetter) {
      // Correct!
      const points = 20 + (level * 5);
      setScore(s => s + points);
      setCorrectAnswers(c => c + 1);
      
      toast.success(
        <div className="flex items-center gap-2">
          <span className="text-2xl">{letterData[currentLetter].animal}</span>
          <span>+{points} points! {currentLetter} is for {letterData[currentLetter].word}!</span>
        </div>
      );
      
      // Level up every 5 correct
      if ((correctAnswers + 1) % 5 === 0) {
        setLevel(l => l + 1);
        toast.success(`Level ${level + 1}! Great job! 🌟`);
      }
      
      generateQuestion();
    } else {
      // Wrong - just show feedback, no lives system with timed mode
      toast.error(`Not quite! That letter makes the "${letterData[selectedLetter].sound}" sound.`);
    }
  };

  // Save game on finish
  useEffect(() => {
    if (gameState === 'finished') {
      const saveGame = async () => {
        try {
          await api.post('/games/complete', {
            game_id: 'letter-sounds-safari',
            score: score,
            time_seconds: 0,
            metadata: { level, correct_answers: correctAnswers }
          });
        } catch (error) {
          console.error('Error saving game:', error);
        }
      };
      saveGame();
    }
  }, [gameState, score, level, correctAnswers, api]);

  return (
    <StudentLayout>
      <div data-testid="letter-sounds-safari-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-emerald-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          {gameState === 'playing' && (
            <div className="flex items-center gap-4">
              <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${timeLeft <= 10 ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                <span className="font-heading text-xl font-bold">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
              </div>
              <div className="flex items-center gap-2 bg-amber-100 px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-600" />
                <span className="font-heading text-xl font-bold text-amber-700">{score}</span>
              </div>
            </div>
          )}
        </div>

        {/* Game Title */}
        <div className="student-card bg-gradient-to-r from-emerald-100 to-green-100 border-emerald-300 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-emerald-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center text-3xl">
              🦁
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Letter Sounds Safari</h1>
              <p className="text-slate-600">
                {gameState === 'playing' ? `Level ${level} • Match letters to sounds!` : 'Learn letter sounds!'}
              </p>
            </div>
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-emerald-400 to-green-500">
            <div className="flex justify-center gap-4 mb-6 text-6xl">
              <span className="animate-bounce" style={{ animationDelay: '0s' }}>🦁</span>
              <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>🐘</span>
              <span className="animate-bounce" style={{ animationDelay: '0.4s' }}>🐯</span>
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">Letter Sounds Safari</h2>
            <p className="text-lg text-emerald-100 mb-6">
              Listen to the sound and find the matching letter!
            </p>
            <div className="text-emerald-100 mb-8 space-y-2">
              <p>🔊 Click the speaker to hear the sound</p>
              <p>🔤 Click the letter that makes that sound</p>
              <p>⭐ Earn points for correct answers!</p>
            </div>
            <Button onClick={startGame} className="bg-white text-emerald-600 hover:bg-emerald-50 text-lg px-10 py-6">
              Start Safari!
              <Cat className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentLetter && (
          <div className="student-card bg-gradient-to-b from-emerald-400 to-green-500 min-h-[400px]">
            {/* Safari Background Decoration */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="text-8xl absolute top-4 left-4">🌴</div>
              <div className="text-8xl absolute bottom-4 right-4">🌿</div>
            </div>
            
            {/* Question Area */}
            <div className="text-center mb-8 relative z-10">
              <p className="text-emerald-100 text-lg mb-4">Listen and find the letter!</p>
              
              {/* Sound Button */}
              <button
                onClick={() => playSound(currentLetter)}
                disabled={isPlaying}
                className={`w-32 h-32 bg-white rounded-full shadow-lg flex items-center justify-center mx-auto mb-4 transition-all ${
                  isPlaying ? 'animate-pulse scale-110' : 'hover:scale-105'
                }`}
              >
                <Volume2 className={`w-16 h-16 ${isPlaying ? 'text-emerald-500' : 'text-emerald-600'}`} />
              </button>
              
              <p className="text-white text-xl font-bold">
                {isPlaying ? '🔊 Playing...' : '👆 Tap to hear the sound!'}
              </p>
              
              {/* Hint */}
              <div className="mt-4 bg-white/20 rounded-xl p-3 inline-block">
                <span className="text-5xl">{letterData[currentLetter].animal}</span>
                <p className="text-white text-sm mt-1">This is a {letterData[currentLetter].word}!</p>
              </div>
            </div>

            {/* Letter Options */}
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto relative z-10">
              {options.map((letter) => (
                <button
                  key={letter}
                  onClick={() => handleAnswer(letter)}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:scale-105 transition-all active:scale-95"
                >
                  <span className="text-5xl font-heading font-bold text-emerald-600">{letter}</span>
                  <p className="text-emerald-400 text-sm mt-2">{letterData[letter].sound}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-emerald-400 to-green-500">
            <div className="w-24 h-24 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-14 h-14 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-2">Safari Complete!</h2>
            <p className="text-xl text-emerald-100 mb-4">
              You reached Level {level}!
            </p>
            
            <div className="flex justify-center gap-4 my-8 text-4xl">
              {['🦁', '🐘', '🐯', '🐵', '🦓'].slice(0, Math.min(correctAnswers, 5)).map((animal, i) => (
                <span key={i} className="animate-bounce" style={{ animationDelay: `${i * 0.1}s` }}>{animal}</span>
              ))}
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-300 fill-amber-300" />
              <span className="font-heading text-4xl font-bold text-white">+{score}</span>
              <span className="text-emerald-100 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={startGame} className="border-white text-white hover:bg-white/20">
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="bg-white text-emerald-600 hover:bg-emerald-50">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default LetterSoundsSafariGame;
